import type { MouseEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';

function readWidth(storageKey: string, defaultWidth: number, minWidth: number, maxWidth: number): number {
  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed = raw ? Number.parseInt(raw, 10) : defaultWidth;
    if (!Number.isFinite(parsed)) {
      return defaultWidth;
    }

    return Math.min(maxWidth, Math.max(minWidth, parsed));
  } catch {
    return defaultWidth;
  }
}

function clampWidth(width: number, minWidth: number, maxWidth: number): number {
  return Math.min(maxWidth, Math.max(minWidth, Math.round(width)));
}

export function useResizablePanelWidth(
  storageKey: string,
  defaultWidth: number,
  minWidth: number,
  maxWidth: number,
) {
  const [width, setWidth] = useState(() => readWidth(storageKey, defaultWidth, minWidth, maxWidth));
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, String(width));
    } catch {
      // ignore storage failures
    }
  }, [storageKey, width]);

  const beginResize = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = width;

      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      setIsResizing(true);

      function onMouseMove(moveEvent: globalThis.MouseEvent): void {
        const nextWidth = clampWidth(startWidth + (moveEvent.clientX - startX), minWidth, maxWidth);
        setWidth(nextWidth);
      }

      function onMouseUp(): void {
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        setIsResizing(false);
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      }

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [maxWidth, minWidth, width],
  );

  return { width, isResizing, beginResize };
}
